// ======================================
// Legal Pages Module
// ======================================

export function renderLegalPage(type = 'privacy') {
    const pages = {
        privacy: {
            title: 'Privacy Policy',
            lastUpdated: 'January 2026',
            content: `
        <h2>1. Information We Collect</h2>
        <p>At Namma Veetu Interiors, we collect the following types of information:</p>
        <ul>
          <li><strong>Personal Information:</strong> Name, email address, phone number, and address when you submit contact forms, sign up for an account, or book a consultation.</li>
          <li><strong>Property Information:</strong> Property type, size, city, and budget range to provide accurate quotes and services.</li>
          <li><strong>Usage Data:</strong> Pages visited, time spent on the site, and interaction data collected via cookies and analytics tools.</li>
          <li><strong>Payment Information:</strong> Processed securely through Razorpay. We do not store your card details.</li>
        </ul>

        <h2>2. How We Use Your Information</h2>
        <ul>
          <li>To provide interior design consultations and services</li>
          <li>To generate cost estimates and project proposals</li>
          <li>To communicate about project progress and updates</li>
          <li>To improve our website and services</li>
          <li>To send promotional content (only with your consent)</li>
        </ul>

        <h2>3. Data Sharing</h2>
        <p>We do not sell your personal data. We may share limited data with:</p>
        <ul>
          <li>Our design and installation partners (for project execution)</li>
          <li>Payment processing services (Razorpay)</li>
          <li>Analytics providers (Google Analytics)</li>
        </ul>

        <h2>4. Data Security</h2>
        <p>We use industry-standard encryption (SSL/TLS) and secure servers to protect your data. Access is restricted to authorized personnel only.</p>

        <h2>5. Your Rights</h2>
        <p>You have the right to access, update, or delete your personal information. Contact us at <a href="mailto:privacy@nammaveetuinteriors.com">privacy@nammaveetuinteriors.com</a> for any data requests.</p>

        <h2>6. Cookies</h2>
        <p>We use cookies to enhance your browsing experience. You can control cookie settings in your browser preferences.</p>

        <h2>7. Contact</h2>
        <p>For privacy-related inquiries, email us at <a href="mailto:privacy@nammaveetuinteriors.com">privacy@nammaveetuinteriors.com</a>.</p>
      `
        },
        terms: {
            title: 'Terms of Service',
            lastUpdated: 'January 2026',
            content: `
        <h2>1. Acceptance of Terms</h2>
        <p>By using the Namma Veetu Interiors website and services, you agree to these Terms of Service. If you do not agree, please do not use our services.</p>

        <h2>2. Services</h2>
        <p>Namma Veetu Interiors provides interior design, modular furniture, renovation, and related services. All services are subject to availability in your city.</p>

        <h2>3. Quotations & Pricing</h2>
        <ul>
          <li>Online cost estimates are approximate and subject to on-site verification</li>
          <li>Final pricing is confirmed in the Bill of Quantities (BOQ)</li>
          <li>Prices include GST unless otherwise specified</li>
          <li>Quoted prices are valid for 30 days from the date of issue</li>
        </ul>

        <h2>4. Payment Terms</h2>
        <ul>
          <li>10% advance booking amount at contract signing</li>
          <li>40% upon design approval</li>
          <li>40% at the start of installation</li>
          <li>10% upon final handover and satisfaction</li>
        </ul>

        <h2>5. Project Timeline</h2>
        <p>Timelines provided are estimates. Delays due to force majeure, client-side dependencies, or material availability are excluded from guaranteed timelines.</p>

        <h2>6. Warranty</h2>
        <p>We provide a 10-year warranty on modular installations. The warranty covers manufacturing defects and structural issues. It does not cover damage from misuse, water damage from external sources, or natural wear and tear.</p>

        <h2>7. Cancellation & Refunds</h2>
        <ul>
          <li>Cancellation before design approval: Full refund minus design fee</li>
          <li>Cancellation after manufacturing begins: 50% refund</li>
          <li>Cancellation after installation begins: No refund</li>
        </ul>

        <h2>8. Limitation of Liability</h2>
        <p>Namma Veetu Interiors' liability is limited to the contract value. We are not liable for indirect, incidental, or consequential damages.</p>

        <h2>9. Governing Law</h2>
        <p>These terms are governed by the laws of India, with jurisdiction in the courts of Chennai, Tamil Nadu.</p>
      `
        },
        refund: {
            title: 'Refund Policy',
            lastUpdated: 'January 2026',
            content: `
        <h2>1. Overview</h2>
        <p>We want you to be completely satisfied. This policy outlines our refund process.</p>

        <h2>2. Consultation Fee</h2>
        <p>The initial design consultation is free. If a design fee is charged for advanced 3D renders, it is non-refundable but adjustable against the project cost.</p>

        <h2>3. Project Cancellation Refunds</h2>
        <ul>
          <li><strong>Before Design Approval:</strong> Full refund of advance within 7 working days</li>
          <li><strong>After Design Approval, Before Manufacturing:</strong> 75% refund within 15 working days</li>
          <li><strong>During Manufacturing:</strong> 50% refund within 15 working days</li>
          <li><strong>During/After Installation:</strong> No refund applicable</li>
        </ul>

        <h2>4. Defective Work</h2>
        <p>If any installed item is defective, we will repair or replace it at no additional cost under our warranty. If repair is not possible, a proportional refund will be issued.</p>

        <h2>5. How to Request a Refund</h2>
        <p>Email <a href="mailto:support@nammaveetuinteriors.com">support@nammaveetuinteriors.com</a> with your project ID and reason for the refund request. Our team will respond within 48 hours.</p>
      `
        }
    };

    const page = pages[type] || pages.privacy;

    return `
    <section class="page-header" style="background: var(--color-surface-warm);">
      <div class="container">
        <div class="page-header-content" style="text-align:center;">
          <h1 class="page-title">${page.title}</h1>
          <p class="page-subtitle" style="color: var(--color-text-muted);">Last Updated: ${page.lastUpdated}</p>
        </div>
      </div>
    </section>

    <section style="padding: var(--space-12) 0 var(--space-20);">
      <div class="container" style="max-width: var(--max-width-narrow);">
        <div style="display: flex; gap: var(--space-4); margin-bottom: var(--space-10); border-bottom: 1px solid var(--color-border); padding-bottom: var(--space-4);">
          <a href="/privacy" class="btn ${type === 'privacy' ? 'btn-primary' : 'btn-outline'}" style="border-radius: var(--radius-full);">Privacy Policy</a>
          <a href="/terms" class="btn ${type === 'terms' ? 'btn-primary' : 'btn-outline'}" style="border-radius: var(--radius-full);">Terms of Service</a>
          <a href="/refund-policy" class="btn ${type === 'refund' ? 'btn-primary' : 'btn-outline'}" style="border-radius: var(--radius-full);">Refund Policy</a>
        </div>
        <div class="legal-content" style="line-height: 1.8; color: var(--color-text-secondary);">
          ${page.content}
        </div>
      </div>
    </section>
  `;
}

export function initLegalPage() {
    // Style the legal content headings
    document.querySelectorAll('.legal-content h2').forEach(h => {
        h.style.fontFamily = 'var(--font-heading)';
        h.style.fontSize = 'var(--text-xl)';
        h.style.marginTop = 'var(--space-10)';
        h.style.marginBottom = 'var(--space-4)';
        h.style.color = 'var(--color-text)';
    });
    document.querySelectorAll('.legal-content ul').forEach(ul => {
        ul.style.paddingLeft = 'var(--space-6)';
        ul.style.marginBottom = 'var(--space-4)';
    });
    document.querySelectorAll('.legal-content li').forEach(li => {
        li.style.marginBottom = 'var(--space-2)';
    });
    document.querySelectorAll('.legal-content a').forEach(a => {
        a.style.color = 'var(--color-primary)';
        a.style.textDecoration = 'underline';
    });
}
